import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { Pergunta, Pesquisa, Usuario, FormularioData } from '../types';
import { pesquisaService, perguntaService, usuarioService, respostaService, sessaoService, feedbackService } from '../services/api';
import { validarCPF } from '../utils/cpfValidator';
import QuestionRenderer from './QuestionRenderer';
import ProgressBar from './ProgressBar';
import DadosPessoais from './DadosPessoais';
import TelaApresentacao from './TelaApresentacao';
import TelaLGPD from './TelaLGPD';
import FeedbacksDisplay from './FeedbacksDisplay';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    outline: 0;
    box-sizing: border-box;
  }
  
  html, body, #root {
    width: 100%;
    background: #ffffff;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
  }
  
  #root {
    color: #333;
    min-height: 100vh;
  }
`;

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #ffffff;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div<{ logo?: string }>`
  width: 100%;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  ${props => props.logo && `
    background-image: url(${props.logo});
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
  `}
  
  @media (max-width: 768px) {
    box-shadow: none;
    background-size: 100% auto;
    background-position: center top;
    background-repeat: no-repeat;
    background-attachment: fixed;
    height: 50px;
  }
`;

const Body = styled.div`
  flex: 1;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0;
  width: 100%;
  box-sizing: border-box;
`;

const FormContainer = styled.div`
  background: white;
  padding: 0;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 40px 20px;
  gap: 20px;
  padding: 30px 0;
  border-top: 1px solid #e9ecef;
  
  @media (max-width: 480px) {
    flex-direction: column;
    margin: 40px 20px;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 14px 28px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 140px;
  
  ${props => {
    if (props.variant === 'primary') {
      return `
        background: #222222;
        color: #fff;
        
      `;
    } else if (props.variant === 'secondary') {
      return `
        background: #f8f9fa;
        color: #495057;
        border: 1px solid #dee2e6;
        
      `;
    } else if (props.variant === 'danger') {
      return `
        background: #dc3545;
        color: #fff;
        
      `;
    } else {
      return `
        background: #222222;
        color: #fff;
        
      `;
    }
  }}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;


const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 16px 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #c3e6cb;
  font-size: 14px;
`;


const isSessaoConcluidaLocal = (sessaoLocal: any, pesquisaId?: string): boolean => {
  if (!sessaoLocal) return false;
  const status = sessaoLocal.status;
  const matchesPesquisa = !pesquisaId || sessaoLocal.pesquisa_id === pesquisaId;
  return matchesPesquisa && (status === 'concluido' || status === 'concluida');
};

const FormularioPesquisa: React.FC = () => {
  const { id: pesquisaId } = useParams<{ id: string }>();
  const [pesquisa, setPesquisa] = useState<Pesquisa | null>(null);
  const [perguntas, setPerguntas] = useState<Pergunta[]>([]);
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [usuariosCriados, setUsuariosCriados] = useState<any[]>([]);
  const [formData, setFormData] = useState<FormularioData>({
    usuario: {},
    respostas: {}
  });

  const getSessaoFromLocalStorage = () => {
    try {
      // Verificar se localStorage está disponível
      if (typeof Storage === 'undefined') {
        return null;
      }
      
      const sessaoData = localStorage.getItem('sessao');
      
      if (sessaoData) {
        const parsed = JSON.parse(sessaoData);
        return parsed;
      }
    } catch (error) {
      console.error('Erro ao recuperar sessão do localStorage:', error);
    }
    return null;
  };

  const getUsuariosFromLocalStorage = () => {
    try {
      const usuariosData = localStorage.getItem('usuarios_pesquisa');
      if (usuariosData) {
        const parsed = JSON.parse(usuariosData);
        return parsed;
      }
    } catch (error) {
      console.error('Erro ao recuperar usuários do localStorage:', error);
    }
    return null;
  };

  const saveSessaoToLocalStorage = (sessaoData: any) => {
    try {
      localStorage.setItem('sessao', JSON.stringify(sessaoData));
    } catch (error) {
      console.error('Erro ao salvar sessão no localStorage:', error);
    }
  };

  const clearSessaoFromLocalStorage = () => {
    try {
      localStorage.removeItem('sessao');
    } catch (error) {
      console.error('Erro ao remover sessão do localStorage:', error);
    }
  };

  const clearAllData = () => {
    clearSessaoFromLocalStorage();
    setSessao(null);
    setUsuariosCriados([]);
    setUsuario(null);
  };
  
  const [currentScreen, setCurrentScreen] = useState(1);
  const [currentQuestionPage, setCurrentQuestionPage] = useState(1);
  
  const [loading, setLoading] = useState(true);
  const [pesquisaEncerrada, setPesquisaEncerrada] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [tempoInicio, setTempoInicio] = useState<Date | null>(null);
  const [isNavigationBlocked, setIsNavigationBlocked] = useState(false);
  const [hasExistingSession, setHasExistingSession] = useState(false);
  const [sessionData, setSessionData] = useState<any>(null);
  const [sessao, setSessao] = useState<any>(null);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(false);

  const totalQuestionPages = perguntas.length > 0 ? Math.max(...perguntas.map(p => p.pagina || 1)) : 1;
  const normalizeTemplateName = (value?: string | null): string => {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toUpperCase()
      .replace(/[\s_-]+/g, '');
  };
  const hasDass21ResponseScale = (pergunta: Pergunta): boolean => {
    const opcoes = pergunta.opcoes_resposta || [];
    const normalizedOptions = opcoes.map(opcao => normalizeTemplateName(String(opcao)));
    return normalizedOptions.some(opcao => opcao.includes('NAOSEAPLICOU')) &&
      normalizedOptions.some(opcao => opcao.includes('APLICOUSE'));
  };
  const hasDass21QuestionText = (pergunta: Pergunta): boolean => {
    const titulo = normalizeTemplateName(pergunta.titulo);
    const termosDass21 = [
      'ACHEIDIFICILMEACALMAR',
      'MINHABOCASECA',
      'SENTIMENTOPOSITIVO',
      'DIFICULDADEEMRESPIRAR',
      'DIFICULDADENATERINICIATIVA',
      'REAGIRDEFORMAEXAGERADA',
      'SENTITREMORES',
      'BASTANTENERVOSO',
      'ENTRAREMPANICO',
      'NADADESEJAR',
      'ACHEIDIFICILRELAXAR',
      'DEPRESSIVO',
      'VIDANAOTINHASENTIDO'
    ];
    return termosDass21.some(termo => titulo.includes(termo));
  };
  const isDass21 = perguntas.some(pergunta => {
    const nomeTemplate = normalizeTemplateName(pergunta.template?.nome_template);
    const tituloTemplate = normalizeTemplateName(pergunta.template?.titulo);
    return nomeTemplate === 'DASS21' || tituloTemplate === 'DASS21';
  }) || perguntas.filter(pergunta => hasDass21ResponseScale(pergunta) || hasDass21QuestionText(pergunta)).length >= 3;
  const getNomeCorretora = (): string => {
    const corretora = pesquisa?.corretora;
    const possibleNames = [
      typeof corretora === 'string' ? corretora : undefined,
      typeof corretora === 'object' ? corretora?.nome : undefined,
      typeof corretora === 'object' ? corretora?.nome_fantasia : undefined,
      typeof corretora === 'object' ? corretora?.razao_social : undefined,
      pesquisa?.nome_corretora,
      pesquisa?.corretora_nome
    ];

    return possibleNames.find(name => Boolean(name?.trim()))?.trim() || 'corretora responsável';
  };

  const checkExistingSession = async (pesquisaId: string) => {
    try {
      const sessionKey = `session_${pesquisaId}`;
      const savedSession = localStorage.getItem(sessionKey);
      
      if (savedSession) {
        const sessionInfo = JSON.parse(savedSession);
        
        const existingSession = await sessaoService.getByUsuarioAndPesquisa(sessionInfo.usuarioId, pesquisaId);
        
        if (existingSession && existingSession.status === 'pendente') {
          setHasExistingSession(true);
          setSessionData(existingSession);
          setUsuario({ id: sessionInfo.usuarioId } as Usuario);
          setCurrentScreen(4);
          setCurrentQuestionPage(existingSession.ultima_pagina || 1);
          setTempoInicio(new Date(sessionInfo.tempoInicio));
          return true;
        } else {
        }
      } else {
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const saveSessionToLocalStorage = () => {
    if (usuario?.id && pesquisaId) {
      const sessionKey = `session_${pesquisaId}`;
      const sessionInfo = {
        usuarioId: usuario.id,
        pesquisaId: pesquisaId,
        tempoInicio: tempoInicio?.toISOString(),
        currentScreen: 4,
        currentQuestionPage: 1
      };
      localStorage.setItem(sessionKey, JSON.stringify(sessionInfo));
    } else {
    }
  };

  const clearSessionFromLocalStorage = () => {
    if (pesquisaId) {
      const sessionKey = `session_${pesquisaId}`;
      localStorage.removeItem(sessionKey);
    }
  };
  useEffect(() => {
    if (!pesquisaId) {
      setLoading(false);
      return;
    }

    localStorage.removeItem('sessao_suridata');
    localStorage.removeItem('formulario_pesquisa');
    localStorage.removeItem('usuarios_criados');

    const loadData = async () => {
      try {
        setLoading(true);
        const [pesquisaData, perguntasData] = await Promise.all([
          pesquisaService.getById(pesquisaId),
          perguntaService.getByPesquisa(pesquisaId)
        ]);
        
        setPesquisa(pesquisaData);
        setPerguntas(perguntasData.sort((a, b) => a.ordem - b.ordem));
        
        const sessaoSalva = getSessaoFromLocalStorage();
        if (sessaoSalva && sessaoSalva.id && !sessaoSalva.id.startsWith('temp_')) {
          setSessao(sessaoSalva);
        }
        
        await checkExistingSession(pesquisaId);
      } catch (err: any) {
        const code = err?.response?.data?.code;
        if (code === 'PESQUISA_EXPIRADA') {
          setPesquisaEncerrada(true);
          const sessaoLocal = getSessaoFromLocalStorage();
          const usuariosLocal = getUsuariosFromLocalStorage();
          if (isSessaoConcluidaLocal(sessaoLocal, pesquisaId) && Array.isArray(usuariosLocal) && usuariosLocal.length > 0) {
            setSessao(sessaoLocal);
            setUsuariosCriados(usuariosLocal);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [pesquisaId]);

  useEffect(() => {
    if (validationErrors.length > 0) {
      setShowErrorAlert(true);
      const timer = setTimeout(() => {
        setShowErrorAlert(false);
      }, 8000);
      
      return () => clearTimeout(timer);
    }
  }, [validationErrors]);

  const calcularProgresso = (paginaAtual: number, totalPaginas: number): number => {
    const progresso = Math.round((paginaAtual / totalPaginas) * 100);
    return Math.min(progresso, 100);
  };

  const salvarProgressoSessao = async (usuarioId: string, paginaAtual: number, ultimaPerguntaId?: string, status?: string) => {
    try {
      const progresso = calcularProgresso(paginaAtual, totalQuestionPages);
      const tempoAtual = tempoInicio ? Math.floor((Date.now() - tempoInicio.getTime()) / 1000) : 0;
      
           const dadosSessao = {
             status: status || (paginaAtual < totalQuestionPages ? 'pendente' : 'concluido'),
             ultima_pagina: paginaAtual,
             ultima_pergunta_id: ultimaPerguntaId,
             progresso_percentual: progresso,
             tempo_total_segundos: tempoAtual,
             ip_address: window.location.hostname === 'localhost' ? '127.0.0.1' : window.location.hostname,
             user_agent: navigator.userAgent,
             dados_metadados: {
               resolucao: `${window.screen.width}x${window.screen.height}`,
               idioma: navigator.language,
               timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
             }
           };

      const sessaoAtualizada = await sessaoService.createOrUpdate(usuarioId, pesquisaId!, dadosSessao);
      setSessao(sessaoAtualizada);
    } catch (error) {
    }
  };

  const focusFirstErrorField = () => {
    setTimeout(() => {
      if (currentScreen === 3) {
        const { usuario } = formData;
        
        if (!usuario.sexo) {
          const sexoInput = document.querySelector('input[name="sexo"]') as HTMLInputElement;
          if (sexoInput) {
            sexoInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            sexoInput.focus();
            return;
          }
        }
        
        if (!usuario.nascimento) {
          const nascimentoInput = document.querySelector('input[type="date"]') as HTMLInputElement;
          if (nascimentoInput) {
            nascimentoInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            nascimentoInput.focus();
            return;
          }
        }
        
        if (!isDass21) {
          if (!usuario.peso || usuario.peso <= 0) {
            const pesoInput = document.querySelector('input[placeholder="70"]') as HTMLInputElement;
            if (pesoInput) {
              pesoInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
              pesoInput.focus();
              return;
            }
          }
          
          if (!usuario.altura || usuario.altura <= 0) {
            const alturaInput = document.querySelector('input[placeholder="1.70"]') as HTMLInputElement;
            if (alturaInput) {
              alturaInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
              alturaInput.focus();
              return;
            }
          }
        }
        
        if (!pesquisa?.mapeamento_anonimizado) {
          if (!usuario.nome || usuario.nome.trim() === '') {
            const nomeInput = document.querySelector('input[placeholder*="nome"]') as HTMLInputElement;
            if (nomeInput) {
              nomeInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
              nomeInput.focus();
              return;
            }
          }
          
          if (!usuario.cpf || usuario.cpf.trim() === '') {
            const cpfInput = document.querySelector('input[placeholder*="000.000.000"]') as HTMLInputElement;
            if (cpfInput) {
              cpfInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
              cpfInput.focus();
              return;
            }
          }
        }
      }
      
      if (currentScreen >= 4) {
        const perguntasAtuais = perguntas.filter(p => (p.pagina || 1) === currentQuestionPage);
        for (const pergunta of perguntasAtuais) {
          if (pergunta.obrigatorio) {
            const resposta = formData.respostas[pergunta.id];
            if (!resposta || (Array.isArray(resposta) && resposta.length === 0) || (typeof resposta === 'string' && resposta.trim() === '')) {
              const questionElement = document.querySelector(`[data-pergunta-id="${pergunta.id}"]`);
              if (questionElement) {
                const firstInput = questionElement.querySelector('input, textarea') as HTMLInputElement;
                if (firstInput) {
                  firstInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  firstInput.focus();
                  return;
                }
              }
            }
          }
        }
      }
    }, 200);
  };

  const handleUsuarioChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      usuario: {
        ...prev.usuario,
        [field]: value
      }
    }));
  };

  const handleSessaoEncontrada = async (sessao: any) => {
    if (sessao && sessao.status === 'pendente') {
      try {
        let usuarioCompleto: any = null;
        
        if (sessao.usuarios && Array.isArray(sessao.usuarios) && sessao.usuarios.length > 0) {
          usuarioCompleto = sessao.usuarios[0];
        } 
        else if (sessao.usuario_id) {
          usuarioCompleto = await usuarioService.getById(sessao.usuario_id);
        }
        else {
          const cpf = formData.usuario.cpf;
          if (!cpf || !pesquisaId) {
            setValidationErrors(['Erro: Dados incompletos.']);
            return;
          }
          
          const usuarioExistente = await usuarioService.getByCpfAndPesquisa(cpf, pesquisaId);
          
          if (!usuarioExistente || !Array.isArray(usuarioExistente) || usuarioExistente.length === 0) {
            setValidationErrors(['Erro: Usuário não encontrado.']);
            return;
          }
          
          usuarioCompleto = usuarioExistente[0];
        }
        
        if (!usuarioCompleto || !usuarioCompleto.id) {
          setValidationErrors(['Erro: Usuário inválido.']);
          return;
        }
        
        const respostasExistentes = await respostaService.getByUsuarioAndPesquisa(usuarioCompleto.id, pesquisaId!);
        
        if (usuarioCompleto) {
          const usuariosData = [usuarioCompleto];
          localStorage.setItem('usuarios_pesquisa', JSON.stringify(usuariosData));
          
          setFormData(prev => {
            const novoFormData = {
              ...prev,
              usuario: {
                ...prev.usuario,
                ...usuarioCompleto
              }
            };
            return novoFormData;
          });
          
          setUsuario(usuarioCompleto);
          
          try {
            const usuarioExistente = await usuarioService.getById(usuarioCompleto.id);
            if (!usuarioExistente) {
              const usuarioCriado = await usuarioService.create({
                ...usuarioCompleto,
                pesquisa_id: pesquisaId,
                sessao_id: sessao.id
              });
              // Atualizar o ID do usuário com o ID criado no banco
              usuarioCompleto.id = usuarioCriado.id;
              setUsuario(usuarioCompleto);
            }
          } catch (error) {
            console.error('Erro ao verificar/criar usuário no banco:', error);
          }
        }
        
        if (respostasExistentes && Array.isArray(respostasExistentes) && respostasExistentes.length > 0) {
          const respostasPorPergunta: { [key: string]: string[] } = {};
          respostasExistentes.forEach((resposta: any) => {
            if (!respostasPorPergunta[resposta.pergunta_id]) {
              respostasPorPergunta[resposta.pergunta_id] = [];
            }
            respostasPorPergunta[resposta.pergunta_id].push(resposta.resposta);
          });
          
          const respostasFormatadas: { [key: string]: any } = {};
          Object.entries(respostasPorPergunta).forEach(([perguntaId, respostas]) => {
            if (respostas.length === 1) {
              respostasFormatadas[perguntaId] = respostas[0];
            } else {
              respostasFormatadas[perguntaId] = respostas;
            }
          });
          
          setFormData(prev => ({
            ...prev,
            respostas: respostasFormatadas
          }));
          
        }
        
        localStorage.setItem('sessao', JSON.stringify(sessao));
        
        setTimeout(() => {
          const ultimaPagina = sessao.ultima_pagina || 1;
          setCurrentQuestionPage(ultimaPagina);
          setCurrentScreen(4);
          setValidationErrors([]);
          setSuccess('Sessão carregada com sucesso! Continue de onde parou.');
          
          setTimeout(() => {
            setSuccess('');
          }, 3000);
        }, 500);
        
      } catch (error) {
        console.error('Erro ao carregar sessão pendente:', error);
        setValidationErrors(['Erro ao carregar sessão pendente. Tente novamente.']);
      }
    }
  };


  const handleRespostaChange = (perguntaId: string, resposta: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      respostas: {
        ...prev.respostas,
        [perguntaId]: resposta
      }
    }));
  };

  const validateDadosPessoais = (): string[] => {
    const errors: string[] = [];
    const { usuario } = formData;
    
    if (!usuario.sexo) {
      errors.push('Sexo é obrigatório');
    }
    if (!usuario.nascimento) {
      errors.push('Data de nascimento é obrigatória');
    }
    if (!isDass21) {
      if (!usuario.peso || usuario.peso <= 0) {
        errors.push('Peso é obrigatório');
      }
      if (!usuario.altura || usuario.altura <= 0) {
        errors.push('Altura é obrigatória');
      }
      
      if (usuario.altura && usuario.altura > 2.5) {
        errors.push('Altura deve ser menor que 2.5m');
      }
      if (usuario.peso && usuario.peso > 500) {
        errors.push('Peso deve ser menor que 500kg');
      }
    }
    
    if (!pesquisa?.mapeamento_anonimizado) {
      if (!usuario.nome || usuario.nome.trim() === '') {
        errors.push('Nome é obrigatório');
      }
      if (!usuario.cpf || usuario.cpf.trim() === '') {
        errors.push('CPF é obrigatório');
      } else if (!validarCPF(usuario.cpf)) {
        errors.push('CPF inválido. Verifique os dígitos.');
      }
      if (!usuario.email || usuario.email.trim() === '') {
        errors.push('E-mail é obrigatório');
      }
      if (!usuario.celular || usuario.celular.trim() === '') {
        errors.push('Celular é obrigatório');
      }
    }
    
    if (usuario.numero_dependentes && usuario.numero_dependentes > 0 && !pesquisa?.somente_titulares) {
      for (let i = 1; i <= usuario.numero_dependentes; i++) {
        const depSexo = (usuario as any)[`dependente_${i}_sexo`];
        const depNascimento = (usuario as any)[`dependente_${i}_nascimento`];
        const depPeso = (usuario as any)[`dependente_${i}_peso`];
        const depAltura = (usuario as any)[`dependente_${i}_altura`];
        
        if (!depSexo) {
          errors.push(`Sexo do dependente ${i} é obrigatório`);
        }
        if (!depNascimento) {
          errors.push(`Data de nascimento do dependente ${i} é obrigatória`);
        }
        if (!isDass21) {
          if (!depPeso || depPeso <= 0) {
            errors.push(`Peso do dependente ${i} é obrigatório`);
          }
          if (!depAltura || depAltura <= 0) {
            errors.push(`Altura do dependente ${i} é obrigatória`);
          }
          
          if (depAltura && depAltura > 2.5) {
            errors.push(`Altura do dependente ${i} deve ser menor que 2.5m`);
          }
          if (depPeso && depPeso > 500) {
            errors.push(`Peso do dependente ${i} deve ser menor que 500kg`);
          }
        }
        
        if (!pesquisa?.mapeamento_anonimizado) {
          const depNome = (usuario as any)[`dependente_${i}_nome`];
          const depCpf = (usuario as any)[`dependente_${i}_cpf`];
          
          if (!depNome || depNome.trim() === '') {
            errors.push(`Nome do dependente ${i} é obrigatório`);
          }
          if (!depCpf || depCpf.trim() === '') {
            errors.push(`CPF do dependente ${i} é obrigatório`);
          } else if (!validarCPF(depCpf)) {
            errors.push(`CPF do dependente ${i} é inválido. Verifique os dígitos.`);
          }
        }
      }
    }
    
    return errors;
  };

  const validatePerguntasAtuais = (): string[] => {
    const errors: string[] = [];
    const perguntasAtuais = perguntas.filter(p => (p.pagina || 1) === currentQuestionPage);
    const perguntasParaExibir = perguntasAtuais.filter(pergunta => deveExibirPergunta(pergunta));
    
    perguntasParaExibir.forEach(pergunta => {
      const resposta = formData.respostas[pergunta.id];
      
      if (pergunta.obrigatorio) {
        let isInvalid = false;
        
        if (!resposta) {
          isInvalid = true;
        } else if (Array.isArray(resposta)) {
          isInvalid = resposta.length === 0;
        } else if (typeof resposta === 'string') {
          isInvalid = resposta.trim() === '';
        }
        
        if (isInvalid) {
          errors.push(`Pergunta "${pergunta.titulo}" é obrigatória`);
        }
      }
    });
    
    return errors;
  };


  const deveExibirPergunta = (pergunta: any): boolean => {
    const { usuario } = formData;
    
    
    if (pergunta.pergunta_pai_id && pergunta.respostas_esperadas) {
      const respostaPai = formData.respostas[pergunta.pergunta_pai_id];
      
      if (!respostaPai) {
        return false;
      }
      
      let temRespostaEsperada = false;
      
      if (typeof respostaPai === 'object' && respostaPai !== null && !Array.isArray(respostaPai)) {
        const valoresResposta = Object.values(respostaPai);
        
        temRespostaEsperada = pergunta.respostas_esperadas.some((respostaEsperada: string) => {
          const match = valoresResposta.some((valor: any) => {
            const valorStr = Array.isArray(valor) ? valor.join(',') : String(valor || '');
            return valorStr.includes(respostaEsperada);
          });
          return match;
        });
      } else {
        const respostaPaiStr = Array.isArray(respostaPai) ? respostaPai.join(',') : String(respostaPai || '');
        
        temRespostaEsperada = pergunta.respostas_esperadas.some((respostaEsperada: string) => {
          const match = respostaPaiStr.includes(respostaEsperada);
          return match;
        });
      }
      
      if (!temRespostaEsperada) {
        return false;
      }
    }
    
    const numeroDependentes = usuario.numero_dependentes || 0;
    let usuariosQueAtendem: string[] = [];
    
    
    if (pergunta.pergunta_pai_id && pergunta.respostas_esperadas) {
      const respostaPai = formData.respostas[pergunta.pergunta_pai_id];
      
      if (typeof respostaPai === 'object' && respostaPai !== null && !Array.isArray(respostaPai)) {
        Object.entries(respostaPai).forEach(([usuarioKey, respostaUsuario]) => {
          const respostaStr = Array.isArray(respostaUsuario) ? respostaUsuario.join(',') : String(respostaUsuario || '');
          const usuarioRespondeuCorretamente = pergunta.respostas_esperadas.some((respostaEsperada: string) => {
            return respostaStr.includes(respostaEsperada);
          });
          
          if (usuarioRespondeuCorretamente) {
            
            if (usuarioKey === 'titular') {
              const titularAtende = verificarCondicoesUsuario(pergunta, usuario, 'titular');
              if (titularAtende) {
                usuariosQueAtendem.push('titular');
              }
            } else if (usuarioKey.startsWith('dependente_')) {
              const dependenteIndex = parseInt(usuarioKey.split('_')[1]);
              const dependente = {
                sexo: (usuario as any)[`dependente_${dependenteIndex}_sexo`],
                nascimento: (usuario as any)[`dependente_${dependenteIndex}_nascimento`],
                nome: (usuario as any)[`dependente_${dependenteIndex}_nome`],
                cpf: (usuario as any)[`dependente_${dependenteIndex}_cpf`]
              };
              
              const dependenteAtende = verificarCondicoesUsuario(pergunta, dependente, `dependente_${dependenteIndex}`);
              if (dependenteAtende) {
                usuariosQueAtendem.push(`dependente_${dependenteIndex}`);
              }
            }
          }
        });
      } else {
        const respostaStr = Array.isArray(respostaPai) ? respostaPai.join(',') : String(respostaPai || '');
        const usuarioRespondeuCorretamente = pergunta.respostas_esperadas.some((respostaEsperada: string) => {
          return respostaStr.includes(respostaEsperada);
        });
        
        if (usuarioRespondeuCorretamente) {
          const titularAtende = verificarCondicoesUsuario(pergunta, usuario, 'titular');
          if (titularAtende) {
            usuariosQueAtendem.push('titular');
          }
        }
      }
    } else {
      
      const titularAtende = verificarCondicoesUsuario(pergunta, usuario, 'titular');
      if (titularAtende) {
        usuariosQueAtendem.push('titular');
      }
      
      
      for (let i = 1; i <= numeroDependentes; i++) {
        const dependente = {
          sexo: (usuario as any)[`dependente_${i}_sexo`],
          nascimento: (usuario as any)[`dependente_${i}_nascimento`],
          nome: (usuario as any)[`dependente_${i}_nome`],
          cpf: (usuario as any)[`dependente_${i}_cpf`]
        };
        
        const dependenteAtende = verificarCondicoesUsuario(pergunta, dependente, `dependente_${i}`);
        if (dependenteAtende) {
          usuariosQueAtendem.push(`dependente_${i}`);
        }
      }
    }
    
    if (usuariosQueAtendem.length === 0) {
      return false;
    }
    
    pergunta.usuariosQueAtendem = usuariosQueAtendem;
    
    
    return true;
  };

  const verificarCondicoesUsuario = (pergunta: any, usuario: any, tipoUsuario: string): boolean => {
    
    if (pergunta.genero_esperado && usuario.sexo !== pergunta.genero_esperado) {
      return false;
    }
    
    if (usuario.nascimento && (pergunta.idade_minima || pergunta.idade_maxima)) {
      const dataNascimento = new Date(usuario.nascimento);
      const hoje = new Date();
      const idade = hoje.getFullYear() - dataNascimento.getFullYear();
      const mesAtual = hoje.getMonth();
      const mesNascimento = dataNascimento.getMonth();
      
      const idadeReal = mesAtual < mesNascimento || 
        (mesAtual === mesNascimento && hoje.getDate() < dataNascimento.getDate()) 
        ? idade - 1 : idade;
      
      
      if (pergunta.idade_minima && idadeReal < pergunta.idade_minima) {
        return false;
      }
      
      if (pergunta.idade_maxima && idadeReal > pergunta.idade_maxima) {
        return false;
      }
    }
    
    return true;
  };

  const handleNextScreen = () => {
    if (isNavigationBlocked) {
      return;
    }
    
    let errors: string[] = [];
    
    if (currentScreen === 3) {
      errors = validateDadosPessoais();
    }
    
    if (errors.length > 0) {
      setValidationErrors(errors);
      focusFirstErrorField();
      return;
    }
    
    setValidationErrors([]);
    
        setCurrentScreen(prev => {
          const newScreen = prev + 1;
          if (newScreen === 4) {
            setCurrentQuestionPage(1);
            setTempoInicio(new Date());
            saveSessionToLocalStorage();
          }
          return newScreen;
        });
    window.scrollTo(0, 0);
  };

  const handlePrevScreen = () => {
    if (currentScreen > 1) {
      setCurrentScreen(prev => prev - 1);
      setValidationErrors([]);
      window.scrollTo(0, 0);
    }
  };



  const handlePrevQuestionPage = () => {
    if (currentQuestionPage > 1) {
      setCurrentQuestionPage(prev => prev - 1);
      setValidationErrors([]);
      window.scrollTo(0, 0);
    }
  };


  const criarUsuarioTitular = async (usuarioAtual: any, sessaoId: string) => {
    
    const usuarioParaEnvio: any = {
      sexo: formData.usuario.sexo,
      nascimento: formData.usuario.nascimento,
      peso: formData.usuario.peso,
      altura: formData.usuario.altura,
      numero_dependentes: formData.usuario.numero_dependentes || 0,
      tipo_usuario: 'titular'
    };
    
    if (!pesquisa?.mapeamento_anonimizado) {
      usuarioParaEnvio.nome = formData.usuario.nome;
      usuarioParaEnvio.cpf = formData.usuario.cpf;
      usuarioParaEnvio.email = formData.usuario.email;
      usuarioParaEnvio.celular = formData.usuario.celular;
    }
    
    Object.keys(usuarioParaEnvio).forEach(key => {
      const value = (usuarioParaEnvio as any)[key];
      if (key !== 'tipo_usuario' && (value === undefined || value === null || value === '')) {
        delete (usuarioParaEnvio as any)[key];
      }
    });
    
    try {
      
      const dadosCompletos = {
        ...usuarioParaEnvio,
        pesquisa_id: pesquisaId,
        sessao_id: sessaoId
      };
      
      
      const novoUsuario = await usuarioService.create(dadosCompletos);
      
      
      if (Array.isArray(novoUsuario)) {
        if (novoUsuario.length > 0 && novoUsuario[0]) {
          return novoUsuario[0];
        } else {
          throw new Error('Falha ao criar usuário titular: array vazio ou primeiro elemento inválido');
        }
      }
      
      if (!novoUsuario || !novoUsuario.id) {
        throw new Error('Falha ao criar usuário titular: resposta inválida da API');
      }
      
      return novoUsuario;
    } catch (error: any) {
      console.error('Erro ao criar usuário titular:', error);
      if (error.response?.status === 409 || error.message?.includes('já existe') || error.message?.includes('duplicate')) {
        const usuarioExistente = await usuarioService.getByCpf(formData.usuario.cpf || '');
        if (usuarioExistente) {
          return usuarioExistente;
        }
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar usuário titular';
      throw new Error(errorMessage);
    }
  };

  const criarUsuarioDependente = async (numeroDependente: number, dadosUsuario: any, sessaoId: string) => {
    const relacionamento = dadosUsuario[`dependente_${numeroDependente}_relacionamento`];
    if (!relacionamento) {
      throw new Error(`Relacionamento é obrigatório para o dependente ${numeroDependente}`);
    }

    const usuarioParaEnvio: any = {
      sexo: dadosUsuario[`dependente_${numeroDependente}_sexo`],
      nascimento: dadosUsuario[`dependente_${numeroDependente}_nascimento`],
      peso: dadosUsuario[`dependente_${numeroDependente}_peso`],
      altura: dadosUsuario[`dependente_${numeroDependente}_altura`],
      relacionamento: relacionamento,
      tipo_usuario: 'dependente'
    };
    
    if (!pesquisa?.mapeamento_anonimizado) {
      usuarioParaEnvio.nome = dadosUsuario[`dependente_${numeroDependente}_nome`];
      usuarioParaEnvio.cpf = dadosUsuario[`dependente_${numeroDependente}_cpf`];
    }
    
    Object.keys(usuarioParaEnvio).forEach(key => {
      const value = (usuarioParaEnvio as any)[key];
      if (key !== 'relacionamento' && key !== 'tipo_usuario' && (value === undefined || value === null || value === '')) {
        delete (usuarioParaEnvio as any)[key];
      }
    });
    
    try {
      
      const dadosCompletos = {
        ...usuarioParaEnvio,
        pesquisa_id: pesquisaId,
        sessao_id: sessaoId
      };
      
      
      const novoUsuario = await usuarioService.create(dadosCompletos);
      
      
      if (Array.isArray(novoUsuario)) {
        if (novoUsuario.length > 0 && novoUsuario[0]) {
          return novoUsuario[0];
        } else {
          throw new Error(`Falha ao criar dependente ${numeroDependente}: array vazio ou primeiro elemento inválido`);
        }
      }
      
      if (!novoUsuario || !novoUsuario.id) {
        throw new Error(`Falha ao criar dependente ${numeroDependente}: resposta inválida da API`);
      }
      
      return novoUsuario;
    } catch (error: any) {
      console.error(`Erro ao criar dependente ${numeroDependente}:`, error);
      if (error.response?.status === 409 || error.message?.includes('já existe') || error.message?.includes('duplicate')) {
        const usuarioExistente = await usuarioService.getByCpf(dadosUsuario[`dependente_${numeroDependente}_cpf`] || '');
        if (usuarioExistente) {
          return usuarioExistente;
        }
      }
      
      const errorMessage = error.response?.data?.message || error.message || `Erro ao criar dependente ${numeroDependente}`;
      throw new Error(errorMessage);
    }
  };

  const processarRespostaParaUsuario = (resposta: any, usuario: any, numeroDependentes: number, indexUsuario: number) => {
    if (numeroDependentes === 0) {
      return resposta;
    }

    if (typeof resposta === 'object' && resposta !== null && !Array.isArray(resposta)) {
      
      let chaveUsuario = '';
      
      if (usuario.tipo_usuario === 'titular') {
        chaveUsuario = 'titular';
      } else if (usuario.tipo_usuario === 'dependente') {
        chaveUsuario = `dependente_${indexUsuario}`;
      }
      
      const respostaParaUsuario = resposta[chaveUsuario] || null;
      return respostaParaUsuario;
    }

    return resposta;
  };

  const handleSubmit = async () => {
    try {
      
      let sessaoIdFinal: string | null = null;
      setSaving(true);
      
      
      const allErrors: string[] = [];
      
      const dadosErrors = validateDadosPessoais();
      allErrors.push(...dadosErrors);
      
      const perguntasErrors = validatePerguntasAtuais();
      allErrors.push(...perguntasErrors);
      
      if (allErrors.length > 0) {
        setValidationErrors(allErrors);
        focusFirstErrorField();
        setSaving(false);
        return;
      }
      

      let usuarioAtual = usuario;
      
      

      if (!usuarioAtual || !usuarioAtual.id) {
        throw new Error('Usuário não encontrado');
      }

      let sessaoAtual = getSessaoFromLocalStorage();
      
      // Se não encontrou no localStorage, tenta usar a sessão do estado
      if (!sessaoAtual || !sessaoAtual.id) {
        if (sessao && sessao.id) {
          sessaoAtual = sessao;
        }
      }
      
      // Se ainda não encontrou, tenta buscar diretamente no localStorage com diferentes chaves
      if (!sessaoAtual || !sessaoAtual.id) {
        const chavesPossiveis = ['sessao', 'sessao_pesquisa', 'session'];
        
        for (const chave of chavesPossiveis) {
          try {
            const dados = localStorage.getItem(chave);
            if (dados) {
              const parsed = JSON.parse(dados);
              if (parsed && parsed.id) {
                sessaoAtual = parsed;
                break;
              }
            }
          } catch (error) {
            // Ignorar erro e tentar próxima chave
          }
        }
      }
      
      // Última tentativa: buscar sessão pela API usando o CPF do usuário
      if (!sessaoAtual || !sessaoAtual.id) {
        try {
          const cpfUsuario = usuarioAtual.cpf;
          if (cpfUsuario && pesquisaId) {
            const sessaoEncontrada = await sessaoService.getByCpfAndPesquisa(cpfUsuario, pesquisaId);
            if (sessaoEncontrada && sessaoEncontrada.id) {
              sessaoAtual = sessaoEncontrada;
              // Salvar no localStorage para próximas tentativas
              saveSessaoToLocalStorage(sessaoEncontrada);
            }
          }
        } catch (error) {
          // Ignorar erro da API
        }
      }
      
      if (!sessaoAtual || !sessaoAtual.id) {
        throw new Error('Sessão não encontrada. Por favor, volte ao início e preencha os dados pessoais novamente.');
      }
      
      sessaoIdFinal = sessaoAtual.id;
      
      const usuariosFromStorage = getUsuariosFromLocalStorage();
      if (!usuariosFromStorage || usuariosFromStorage.length === 0) {
        throw new Error('Usuários não encontrados. Por favor, volte ao início e preencha os dados pessoais novamente.');
      }
      
      
      
      const respostas: any[] = [];
      
      usuariosFromStorage.forEach((usuario: any, index: number) => {
        Object.entries(formData.respostas).forEach(([perguntaId, resposta]) => {
          const respostaParaUsuario = processarRespostaParaUsuario(resposta, usuario, formData.usuario.numero_dependentes || 0, index);
          
          if (respostaParaUsuario !== null && respostaParaUsuario !== undefined && respostaParaUsuario !== '') {
            
            if (Array.isArray(respostaParaUsuario)) {
              respostaParaUsuario.forEach((opcao: string) => {
                respostas.push({
                  pergunta_id: perguntaId,
                  usuario_id: usuario.id,
                  pesquisa_id: pesquisaId,
                  sessao_id: sessaoIdFinal,
                  resposta: opcao,
                  resposta_json: null,
                  ip_address: window.location.hostname === 'localhost' ? '127.0.0.1' : window.location.hostname,
                  user_agent: navigator.userAgent,
                  ativo: true
                });
              });
            } else {
              respostas.push({
                pergunta_id: perguntaId,
                usuario_id: usuario.id,
                pesquisa_id: pesquisaId,
                sessao_id: sessaoIdFinal,
                resposta: respostaParaUsuario,
                resposta_json: null,
                ip_address: window.location.hostname === 'localhost' ? '127.0.0.1' : window.location.hostname,
                user_agent: navigator.userAgent,
                ativo: true
              });
            }
          } else {
          }
        });
      });

      if (respostas.length === 0) {
        setValidationErrors(['É necessário responder pelo menos uma pergunta para enviar o formulário']);
        focusFirstErrorField();
        return;
      }

      if (!pesquisaId) {
        throw new Error('ID da pesquisa não encontrado');
      }
      
      // Validar se todas as respostas têm os campos obrigatórios
      const respostasInvalidas = respostas.filter(r => !r.pergunta_id || !r.usuario_id);
      if (respostasInvalidas.length > 0) {
        throw new Error('Algumas respostas não têm campos obrigatórios');
      }
      
      await respostaService.createBatch({
        pesquisa_id: pesquisaId,
        usuario_id: usuarioAtual.id,
        respostas: respostas
      });

      const perguntasAtuais = perguntas.filter(p => (p.pagina || 1) === currentQuestionPage);
      const proximaPagina = currentQuestionPage + 1;
      const ultimaPerguntaId = perguntasAtuais.length > 0 ? perguntasAtuais[perguntasAtuais.length - 1].id : undefined;
      await salvarProgressoSessao(usuarioAtual.id, proximaPagina, ultimaPerguntaId);
      
      if (currentQuestionPage < totalQuestionPages) {
        setCurrentQuestionPage(prev => prev + 1);
        window.scrollTo(0, 0);
      } else {
        await salvarProgressoSessao(usuarioAtual.id, currentQuestionPage, ultimaPerguntaId, 'concluido');
        
        if (!isDass21) {
          try {
            setLoadingFeedbacks(true);
            
            const usuariosFromStorage = getUsuariosFromLocalStorage();
            
            if (usuariosFromStorage && usuariosFromStorage.length > 0) {
              for (const usuario of usuariosFromStorage) {
                try {
                  await feedbackService.generateFeedbacks(
                    usuario.id.toString(),
                    sessaoIdFinal?.toString() || ''
                  );
                } catch (error) {
                }
              }
            }
            
          } catch (error) {
            console.error('Erro geral ao gerar feedbacks:', error);
          } finally {
            setLoadingFeedbacks(false);
          }
        }
        
        clearSessionFromLocalStorage();
        setCurrentScreen(5);
      }
    } catch (err: any) {
      console.error('Erro ao enviar formulário:', err);
      
      let errorMessage = 'Erro ao enviar formulário';
      
      if (err.response && err.response.data) {
        const responseData = err.response.data;
        if (responseData.message) {
          errorMessage = responseData.message;
        } else if (responseData.error) {
          errorMessage = responseData.error;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setValidationErrors([errorMessage]);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <GlobalStyle />
        <Body>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            Carregando...
          </div>
        </Body>
      </Container>
    );
  }

  if (pesquisaEncerrada) {
    const usuariosLocal = getUsuariosFromLocalStorage() || usuariosCriados;
    const sessaoLocal = sessao || getSessaoFromLocalStorage();
    const canShowFeedbacks = isSessaoConcluidaLocal(sessaoLocal, pesquisaId) && Array.isArray(usuariosLocal) && usuariosLocal.length > 0;

    if (canShowFeedbacks) {
      return (
        <Container>
          <GlobalStyle />
          <Body>
            <FeedbacksDisplay
              usuarios={usuariosLocal.map((u: any) => ({
                id: u.id,
                nome: u.nome || '',
                tipo_usuario: u.tipo_usuario || 'titular',
                relacionamento: u.relacionamento
              }))}
              sessaoId={sessaoLocal.id || ''}
              userEmail={usuariosLocal.find((u: any) => u.tipo_usuario === 'titular')?.email}
            />
          </Body>
        </Container>
      );
    }

    return (
      <Container>
        <GlobalStyle />
        <Body>
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: '#721c24',
            fontSize: '18px',
            fontWeight: '500'
          }}>
            Este mapeamento foi encerrado.
          </div>
        </Body>
      </Container>
    );
  }

  if (!pesquisa) {
    return (
      <Container>
        <GlobalStyle />
        <Body>
          <div style={{ 
            padding: '40px', 
            textAlign: 'center', 
            color: '#721c24',
            fontSize: '18px',
            fontWeight: '500'
          }}>
            Pesquisa não encontrada
          </div>
        </Body>
      </Container>
    );
  }

  const renderCurrentScreen = () => {

    switch (currentScreen) {
      case 1:
        return (
          <TelaApresentacao 
            pesquisa={pesquisa}
            onNext={handleNextScreen}
          />
        );

      case 2:
        return (
          <TelaLGPD 
            pesquisa={pesquisa}
            onNext={handleNextScreen}
            onPrev={handlePrevScreen}
          />
        );

      case 3:
        return (
          <div>
            <DadosPessoais
              data={formData.usuario}
              onChange={handleUsuarioChange}
              mapeamentoAnonimizado={pesquisa?.mapeamento_anonimizado}
              isDass21={isDass21}
              pesquisaId={pesquisaId}
              onValidationError={setValidationErrors}
              onBlockNavigation={setIsNavigationBlocked}
              somenteTitulares={pesquisa?.somente_titulares}
              onSessaoEncontrada={handleSessaoEncontrada}
              onNext={handleNextScreen}
              onUsuarioCriado={(usuario) => {
                setUsuario(usuario);
              }}
              onSessaoCriada={(sessao) => {
                setSessao(sessao);
              }}
              validateBeforeSubmit={validateDadosPessoais}
            />
            
          </div>
        );

      default:
        const perguntasAtuais = perguntas.filter(p => (p.pagina || 1) === currentQuestionPage);
        
        const perguntasParaExibir = perguntasAtuais.filter(pergunta => deveExibirPergunta(pergunta));
        
        return (
          <div>
            <ProgressBar 
              currentPage={currentQuestionPage}
              totalPages={totalQuestionPages}
            />

            {perguntasParaExibir.map(pergunta => {
              const respostaAtual = formData.respostas[pergunta.id];
              
              return (
                <QuestionRenderer
                  key={pergunta.id}
                  pergunta={pergunta}
                  value={respostaAtual}
                  onChange={(resposta: any) => handleRespostaChange(pergunta.id, resposta)}
                  numeroDependentes={formData.usuario.numero_dependentes || 0}
                  usuariosQueAtendem={pergunta.usuariosQueAtendem || []}
                  tipoUsuario="Titular"
                />
              );
            })}

            <ButtonContainer>
              {currentQuestionPage > 1 && (
                <Button variant="secondary" onClick={handlePrevQuestionPage}>
                  Anterior
                </Button>
              )}
              
              <Button 
                onClick={handleSubmit}
                disabled={saving}
              >
                {saving ? 'Enviando...' : (currentQuestionPage < totalQuestionPages ? 'Próximo' : 'Enviar')}
              </Button>
            </ButtonContainer>
            
          </div>
        );

      case 5:
        if (isDass21) {
          const empresa = getNomeCorretora();
          
          return (
            <div style={{
              textAlign: 'center',
              padding: '60px 40px',
              background: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              border: '2px solid #cbd5e1',
              maxWidth: '800px',
              margin: '0 auto'
            }}>
              <h1 style={{
                fontSize: '32px',
                fontWeight: '700',
                color: '#222222',
                marginBottom: '20px'
              }}>
                Muito Obrigado!
              </h1>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#495057',
                marginBottom: '24px'
              }}>
                Pesquisa Finalizada
              </h2>
              <p style={{
                fontSize: '18px',
                color: '#6c757d',
                lineHeight: '1.6',
                maxWidth: '620px',
                margin: '0 auto'
              }}>
                Em breve, a {empresa} entrará em contato para agendar o seu suporte psicológico. Se você não receber esse retorno, é possível que algum dado de seu cadastro esteja desatualizado ou incorreto. Caso isso aconteça, por favor, fale conosco pelo número: (11) 91779-0381.
              </p>
            </div>
          );
        }
        
        return (
          <div style={{
            textAlign: 'center',
            padding: '60px 40px',
            background: '#ffffff',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: '2px solid #cbd5e1',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            <h1 style={{
              fontSize: '32px',
              fontWeight: '700',
              color: '#222222',
              marginBottom: '20px'
            }}>
              Muito Obrigado!
            </h1>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#495057',
              marginBottom: '24px'
            }}>
              Pesquisa Finalizada
            </h2>
            <p style={{
              fontSize: '18px',
              color: '#6c757d',
              marginBottom: '32px',
              lineHeight: '1.6',
              maxWidth: '500px',
              margin: '0 auto 32px auto'
            }}>
              Sua participação é muito importante para nós! Os dados coletados nos ajudarão a melhorar nossos serviços e oferecer uma experiência ainda melhor.
            </p>
            
            {loadingFeedbacks ? (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#4a5568',
                background: '#f7fafc',
                borderRadius: '12px',
                border: '2px solid #e2e8f0',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '16px' }}>Carregando...</div>
                <p style={{ fontSize: '16px', fontWeight: '500' }}>Gerando seus feedbacks personalizados...</p>
              </div>
            ) : (
              <div style={{ marginTop: '40px' }}>
                <FeedbacksDisplay
                  usuarios={getUsuariosFromLocalStorage().map((u: any) => {
                    return {
                      id: u.id,
                      nome: u.nome || '',
                      tipo_usuario: u.tipo_usuario || 'titular',
                      relacionamento: u.relacionamento
                    };
                  })}
                  sessaoId={sessao?.id || ''}
                  userEmail={getUsuariosFromLocalStorage().find((u: any) => u.tipo_usuario === 'titular')?.email}
                />
              </div>
            )}
          </div>
        );

    }
  };

  return (
    <Container>
      <GlobalStyle />
      <Header logo={pesquisa.url_logo} />
      <Body>
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <FormContainer>
          {renderCurrentScreen()}
        </FormContainer>
      </Body>
      
      {showErrorAlert && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: '#f8d7da',
          color: '#721c24',
          padding: '16px 20px',
          borderRadius: '12px',
          border: '1px solid #f5c6cb',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          maxWidth: '400px',
          fontWeight: '500'
        }}>
          <button 
            onClick={() => setShowErrorAlert(false)}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'none',
              border: 'none',
              color: '#721c24',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px'
            }}
          >
            ×
          </button>
          <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
          Campos obrigatórios não preenchidos
          </div>
          <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '14px', lineHeight: '1.4' }}>
            {validationErrors.map((error, index) => (
              <li key={index} style={{ marginBottom: '4px' }}>{error}</li>
            ))}
          </ul>
        </div>
      )}
      
      
    </Container>
  );
};

export default FormularioPesquisa;